import {
  invitationKeys,
  memberKeys,
  sessionKeys,
  type Invitation,
  type LedgerRole,
  type Member,
} from '@taler/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'

export interface UseMembershipOptions {
  readonly ledgerId: () => string | undefined
  /** The screen never issues a request for a capability the ledger did not grant. */
  readonly enabled: () => boolean
}

export interface RoleInput {
  readonly subject: string
  readonly role: LedgerRole
}

export interface UseMembership {
  readonly members: ComputedRef<readonly Member[]>
  readonly invitations: ComputedRef<readonly Invitation[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  readonly isAddingMember: ComputedRef<boolean>
  readonly addMemberError: ComputedRef<Error | null>
  addMember(input: RoleInput): void
  readonly isInviting: ComputedRef<boolean>
  readonly inviteError: ComputedRef<Error | null>
  invite(input: RoleInput): void
  readonly revokingId: ComputedRef<string | undefined>
  readonly revokeError: ComputedRef<Error | null>
  revoke(invitationId: string): void
  readonly changingRoleSubject: ComputedRef<string | undefined>
  readonly changeRoleError: ComputedRef<Error | null>
  changeRole(input: RoleInput): void
  readonly removingSubject: ComputedRef<string | undefined>
  readonly removeError: ComputedRef<Error | null>
  remove(subject: string): void
  retry(): void
}

export function useMembership(options: UseMembershipOptions): UseMembership {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const membersQuery = useQuery({
    queryKey: computed(() => memberKeys.list(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.members.list(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  const invitationsQuery = useQuery({
    queryKey: computed(() => invitationKeys.list(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.invitations.list(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  const addMemberMutation = useMutation({
    mutationFn: (input: RoleInput) => api.members.create(options.ledgerId()!, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: memberKeys.list(options.ledgerId()!) }),
  })

  const inviteMutation = useMutation({
    mutationFn: (input: RoleInput) => api.invitations.create(options.ledgerId()!, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: invitationKeys.list(options.ledgerId()!) }),
  })

  const revokeMutation = useMutation({
    mutationFn: (invitationId: string) => api.invitations.revoke(options.ledgerId()!, invitationId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: invitationKeys.list(options.ledgerId()!) }),
  })

  // A role change can be the acting user's own: the ledgers query carries capabilities, and
  // refetching it is what lets the router's own reactive guard move them off a screen the new
  // role can no longer open.
  const afterOwnMembershipChanges = (): void => {
    queryClient.invalidateQueries({ queryKey: memberKeys.list(options.ledgerId()!) })
    queryClient.invalidateQueries({ queryKey: sessionKeys.ledgers() })
  }

  const changeRoleMutation = useMutation({
    mutationFn: (input: RoleInput) => api.members.updateRole(options.ledgerId()!, input.subject, input.role),
    onSuccess: afterOwnMembershipChanges,
  })

  const removeMutation = useMutation({
    mutationFn: (subject: string) => api.members.remove(options.ledgerId()!, subject),
    onSuccess: afterOwnMembershipChanges,
  })

  return {
    members: computed(() => membersQuery.data.value ?? []),
    invitations: computed(() => invitationsQuery.data.value ?? []),
    isLoading: computed(
      () => active.value && (membersQuery.isPending.value || invitationsQuery.isPending.value),
    ),
    error: computed(() => membersQuery.error.value ?? invitationsQuery.error.value),
    retrying: retrying(membersQuery, invitationsQuery),
    isAddingMember: computed(() => addMemberMutation.isPending.value),
    addMemberError: computed(() => addMemberMutation.error.value),
    addMember: (input) => addMemberMutation.mutate(input),
    isInviting: computed(() => inviteMutation.isPending.value),
    inviteError: computed(() => inviteMutation.error.value),
    invite: (input) => inviteMutation.mutate(input),
    revokingId: computed(() =>
      revokeMutation.isPending.value ? (revokeMutation.variables.value as string | undefined) : undefined,
    ),
    revokeError: computed(() => revokeMutation.error.value),
    revoke: (invitationId) => revokeMutation.mutate(invitationId),
    changingRoleSubject: computed(() =>
      changeRoleMutation.isPending.value
        ? (changeRoleMutation.variables.value as RoleInput | undefined)?.subject
        : undefined,
    ),
    changeRoleError: computed(() => changeRoleMutation.error.value),
    changeRole: (input) => changeRoleMutation.mutate(input),
    removingSubject: computed(() =>
      removeMutation.isPending.value ? (removeMutation.variables.value as string | undefined) : undefined,
    ),
    removeError: computed(() => removeMutation.error.value),
    remove: (subject) => removeMutation.mutate(subject),
    retry: () => {
      void membersQuery.refetch()
      void invitationsQuery.refetch()
    },
  }
}
